import { useEffect, useLayoutEffect, useState, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";

export type FloatingAlign = "start" | "end";

interface FloatingPosition {
  top: number;
  left: number;
  minWidth: number;
  /** Set when the surface must not outgrow its anchor. */
  maxWidth: number | undefined;
  maxHeight: number;
  side: "top" | "bottom";
}

const GAP = 6;
const VIEWPORT_PADDING = 10;

/** Pins a fixed-position surface under (or, without room, over) its anchor and keeps it there. */
function useFloatingPosition(open: boolean, anchorRef: RefObject<HTMLElement | null>, floatingRef: RefObject<HTMLElement | null>, align: FloatingAlign, matchWidth: boolean, fitWidth: boolean): FloatingPosition | null {
  const [position, setPosition] = useState<FloatingPosition | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const update = () => {
      const anchor = anchorRef.current;
      const floating = floatingRef.current;
      if (!anchor || !floating) return;
      const a = anchor.getBoundingClientRect();
      const width = fitWidth ? a.width : Math.max(floating.offsetWidth, matchWidth ? a.width : 0);
      const height = floating.scrollHeight;
      const below = window.innerHeight - a.bottom - VIEWPORT_PADDING - GAP;
      const above = a.top - VIEWPORT_PADDING - GAP;
      const side = below < Math.min(height, 260) && above > below ? "top" : "bottom";
      const room = side === "bottom" ? below : above;
      const visible = Math.min(height, room);
      const rawLeft = align === "start" ? a.left : a.right - width;
      const left = Math.min(Math.max(rawLeft, VIEWPORT_PADDING), Math.max(VIEWPORT_PADDING, window.innerWidth - width - VIEWPORT_PADDING));
      const top = side === "bottom" ? a.bottom + GAP : a.top - GAP - visible;
      setPosition({ top, left, minWidth: matchWidth || fitWidth ? a.width : 0, maxWidth: fitWidth ? a.width : undefined, maxHeight: Math.max(120, room), side });
    };
    update();
    window.addEventListener("resize", update);
    document.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      document.removeEventListener("scroll", update, true);
    };
  }, [open, anchorRef, floatingRef, align, matchWidth, fitWidth]);

  return position;
}

/**
 * A surface that floats over the page from an anchor: Select lists, menus, the date picker. It
 * lives in a portal, closes on an outside press, and keeps Modal's Escape handler away from it.
 */
export function Floating({ open, anchorRef, floatingRef, onDismiss, align = "start", matchWidth = false, fitWidth = false, className = "", children, ...aria }: {
  open: boolean;
  anchorRef: RefObject<HTMLElement | null>;
  floatingRef: RefObject<HTMLDivElement | null>;
  onDismiss: () => void;
  align?: FloatingAlign;
  matchWidth?: boolean;
  /** Pin the width to the anchor's, so long content truncates instead of spilling past a narrow container. */
  fitWidth?: boolean;
  className?: string;
  children: ReactNode;
  id?: string;
  role?: string;
  "aria-label"?: string;
}) {
  const position = useFloatingPosition(open, anchorRef, floatingRef, align, matchWidth, fitWidth);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (anchorRef.current?.contains(target) || floatingRef.current?.contains(target)) return;
      onDismiss();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () => document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, anchorRef, floatingRef, onDismiss]);

  if (typeof document === "undefined") return null;
  return createPortal(<AnimatePresence>
    {open && <motion.div
      ref={floatingRef}
      className={`ot-floating ${className}`.trim()}
      data-modal-escape-boundary="true"
      data-side={position?.side ?? "bottom"}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        minWidth: position?.minWidth ?? 0,
        maxWidth: position?.maxWidth,
        maxHeight: position?.maxHeight ?? "none"
      }}
      initial={{ opacity: 0, scale: 0.96, y: position?.side === "top" ? 4 : -4 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97, transition: { duration: 0.12 } }}
      transition={{ type: "spring", stiffness: 640, damping: 40 }}
      onMouseDown={(event) => event.stopPropagation()}
      {...aria}
    >
      {children}
    </motion.div>}
  </AnimatePresence>, document.body);
}
